// Chip. Unified chip component for the design system.
// Replaces the 3 inline variants previously scattered across SaleView
// (filter lg, ChipButton sm) and CustomerAccountView (Tag mini).
// See DESIGN.md §9.4.

import type { CSSProperties, ReactNode } from "react";

export type ChipVariant =
  | "filter"
  | "tag"
  | "tag-warning"
  | "tag-danger"
  | "tag-success"
  | "option";

export type ChipSize = "sm" | "md" | "lg";

type Props = {
  variant?: ChipVariant;
  size?: ChipSize;
  active?: boolean;
  icon?: ReactNode;
  onClick?: () => void;
  children: ReactNode;
  fullWidth?: boolean;
  style?: CSSProperties;
  className?: string;
  title?: string;
  "aria-label"?: string;
};

const sizeStyle: Record<ChipSize, CSSProperties> = {
  sm: {
    padding: "4px 10px",
    fontSize: "var(--font-size-12)",
    minHeight: 28,
    gap: "var(--space-1)",
  },
  md: {
    padding: "6px 12px",
    fontSize: "var(--font-size-13)",
    minHeight: 36,
    gap: "var(--space-2)",
  },
  lg: {
    padding: "8px 14px",
    fontSize: "var(--font-size-14)",
    minHeight: 44,
    gap: "var(--space-2)",
  },
};

const baseStyle: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "var(--radius-pill)",
  fontWeight: 500,
  whiteSpace: "nowrap",
  flexShrink: 0,
  transition: "background 120ms, color 120ms, border-color 120ms",
};

function filterStyle(active: boolean): CSSProperties {
  return active
    ? {
        background: "var(--color-primary)",
        color: "var(--text-inverse)",
        border: "1px solid var(--color-primary)",
      }
    : {
        background: "transparent",
        color: "var(--text-primary)",
        border: "1px solid var(--border-default)",
      };
}

function optionStyle(active: boolean): CSSProperties {
  return active
    ? {
        background: "var(--color-primary)",
        color: "var(--text-inverse)",
        border: "1px solid var(--color-primary)",
      }
    : {
        background: "var(--surface-base)",
        color: "var(--text-primary)",
        border: "1px solid transparent",
      };
}

function tagStyle(variant: ChipVariant): CSSProperties {
  switch (variant) {
    case "tag-warning":
      return {
        background: "var(--color-warning-soft)",
        color: "var(--color-warning)",
        border: "none",
      };
    case "tag-danger":
      return {
        background: "var(--color-danger-soft)",
        color: "var(--color-danger)",
        border: "none",
      };
    case "tag-success":
      return {
        background: "var(--color-success-soft)",
        color: "var(--color-success)",
        border: "none",
      };
    case "tag":
    default:
      return {
        background: "var(--surface-sunken)",
        color: "var(--text-secondary)",
        border: "none",
      };
  }
}

export function Chip({
  variant = "filter",
  size = "md",
  active = false,
  icon,
  onClick,
  children,
  fullWidth,
  style,
  className,
  title,
  "aria-label": ariaLabel,
}: Props) {
  const isToggle = !!onClick && (variant === "filter" || variant === "option");
  const computedStyle: CSSProperties = {
    ...baseStyle,
    ...sizeStyle[size],
    ...(variant === "filter"
      ? filterStyle(active)
      : variant === "option"
        ? optionStyle(active)
        : tagStyle(variant)),
    width: fullWidth ? "100%" : undefined,
    cursor: onClick ? "pointer" : "default",
    ...style,
  };

  const wrappedIcon = icon ? (
    <span
      aria-hidden="true"
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {icon}
    </span>
  ) : null;

  if (!onClick) {
    return (
      <span style={computedStyle} className={className} title={title} aria-label={ariaLabel}>
        {wrappedIcon}
        {children}
      </span>
    );
  }

  const buttonProps = isToggle
    ? ({ "aria-pressed": active, role: "button" } as const)
    : ({} as const);

  return (
    <button
      type="button"
      onClick={onClick}
      style={computedStyle}
      className={className}
      title={title}
      aria-label={ariaLabel}
      {...buttonProps}
    >
      {wrappedIcon}
      {children}
    </button>
  );
}
